import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, type LucideIcon } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { RANGES, nf } from './utils';

export const RangeToggle = ({ days, onChange }: { days: number; onChange: (d: number) => void }) => (
  <ToggleGroup type="single" value={String(days)} onValueChange={(v) => v && onChange(Number(v))} variant="outline" aria-label="Time range">
    {RANGES.map((r) => <ToggleGroupItem key={r} value={String(r)} aria-label={`Last ${r} days`}>{r}d</ToggleGroupItem>)}
  </ToggleGroup>
);

export const Breadcrumbs = ({ items }: { items: Array<{ label: string; to?: string }> }) => (
  <nav aria-label="Breadcrumb" className="mb-2">
    <ol className="flex flex-wrap items-center gap-1 text-sm text-muted-foreground">
      {items.map((it, i) => (
        <li key={it.label} className="flex items-center gap-1">
          {i > 0 && <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />}
          {it.to ? <Link to={it.to} className="rounded hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">{it.label}</Link> : <span aria-current="page" className="font-semibold text-foreground">{it.label}</span>}
        </li>
      ))}
    </ol>
  </nav>
);

export const Kpi = ({ label, value, hint, icon: Icon }: { label: string; value: number | string; hint: string; icon: LucideIcon }) => (
  <Card>
    <CardContent className="p-5">
      <div className="flex items-center justify-between">
        <p className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
        <Icon className="h-4 w-4 text-primary" aria-hidden="true" />
      </div>
      <p className="mt-2 font-heading text-3xl font-bold">{typeof value === 'number' ? nf(value) : value}</p>
      <p className="mt-1 text-xs text-muted-foreground">{hint}</p>
    </CardContent>
  </Card>
);

export const PageFrame = ({ title, subtitle, crumbs, right, children }: { title: string; subtitle?: string; crumbs?: Array<{ label: string; to?: string }>; right?: ReactNode; children: ReactNode }) => (
  <div className="p-6 space-y-6">
    <div className="flex flex-wrap items-end justify-between gap-3">
      <div>
        {crumbs && <Breadcrumbs items={crumbs} />}
        <h1 className="text-2xl font-bold text-foreground">{title}</h1>
        {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {right}
    </div>
    {children}
  </div>
);
